import type { UiLocale } from './locale'

/**
 * Locale-aware formatting for the shared components. Everything goes through `Intl`, which every
 * target (browser, workerd, Node) ships, so there is no formatting dependency to keep in sync.
 */

const BCP47: Record<UiLocale, string> = {
  nl: 'nl-NL',
  en: 'en-GB',
  de: 'de-DE',
  fr: 'fr-FR',
}

export function formatDateTime(value: string, locale: UiLocale): string {
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return value
  return new Intl.DateTimeFormat(BCP47[locale], {
    dateStyle: 'medium',
    timeStyle: 'short',
  }).format(date)
}

export function formatDate(value: string, locale: UiLocale): string {
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return value
  return new Intl.DateTimeFormat(BCP47[locale], { dateStyle: 'medium' }).format(date)
}

export function formatNumber(value: number, locale: UiLocale, unit?: string): string {
  const formatted = new Intl.NumberFormat(BCP47[locale]).format(value)
  return unit === undefined ? formatted : `${formatted} ${unit}`
}

/**
 * An amount in minor units, as money (M5-06). Payment providers count in cents and so does
 * Cargofollow; the division by 100 belongs here, once, rather than in every screen that shows a
 * price. `Intl` places the symbol and the separators, so €499 reads as "€ 499,00" in Dutch and
 * "€499.00" in English without either being hardcoded.
 */
export function formatMoney(minorUnits: number, currency: string, locale: UiLocale): string {
  return new Intl.NumberFormat(BCP47[locale], {
    style: 'currency',
    currency: currency.toUpperCase(),
  }).format(minorUnits / 100)
}

/** Human-readable byte size for the document list; binary prefixes, one decimal from MB up. */
export function formatBytes(bytes: number, locale: UiLocale): string {
  if (bytes < 1024) return `${formatNumber(bytes, locale)} B`
  const kb = bytes / 1024
  if (kb < 1024) return `${new Intl.NumberFormat(BCP47[locale]).format(Math.round(kb))} kB`
  return `${new Intl.NumberFormat(BCP47[locale], { maximumFractionDigits: 1 }).format(kb / 1024)} MB`
}
